/**
 * Loads the backend's first-party web tracker (`/visitor.js`) into the page.
 *
 * The script is requested from the portal's own origin on purpose: the
 * backend resolves the brand from the request host, so a relative src is what
 * attributes the page view to the right clinic.
 *
 * Idempotent. The tag is marked with a data attribute so a remount does not add
 * a second one, and a page where the clinic already loads the tracker (through
 * GTM or by hand) is left alone. The tracker itself also guards against running
 * twice (`window.__dappleTracker`), so a race between the two is harmless.
 */
export const VISITOR_SCRIPT_SRC = "/visitor.js";

const MARKER_ATTRIBUTE = "data-dapple-visitor";

type TrackerWindow = {
  __dappleTracker?: unknown;
  __dappleEntrySearch?: string;
};

/** The cross-domain hand-off params a clinic's website link carries. */
const HANDOFF_KEYS = ["dapple_id", "dapple_sid", "dapple_consent"];

/**
 * The message link token key (backend `MESSAGE_LINKS.md`). It arrives in the
 * URL fragment (`#dapple_rid=…`), never the query string.
 */
const MESSAGE_LINK_KEY = "dapple_rid";

/** The fragment without its message link token ("" when nothing is left). */
const fragmentWithoutToken = (hash: string): string => {
  const rest = hash
    .replace(/^#/, "")
    .split("&")
    .filter(part => part !== "" && !part.startsWith(`${MESSAGE_LINK_KEY}=`))
    .join("&");

  return rest ? `#${rest}` : "";
};

/**
 * Records the entry URL's cross-domain hand-off for `/visitor.js`.
 *
 * The portal can redirect before the tracker loads (`/` to `/profile` to
 * `/signin`), which drops `?dapple_id=…&dapple_sid=…` from the URL and with it
 * the link to the visitor's history on the clinic's website. The tracker reads
 * `window.__dappleEntrySearch` as a fallback for a missing key, and validates
 * each value exactly as it would one read from the URL. Only the hand-off keys
 * are copied, never the rest of the query string.
 *
 * A message link token (`#dapple_rid=` from a clinic's email or SMS) is
 * recorded the same way and then removed from the address bar, so it is never
 * bookmarked, copied or seen by any other script on the page.
 *
 * Runs when this module is first evaluated (`index.web.js` imports it first),
 * which is before the app renders and so before any redirect. Safe without a
 * DOM.
 *
 * @param search The entry query string; defaults to the current URL's.
 * @param hash The entry fragment; defaults to the current URL's.
 */
export const recordEntryHandoff = (
  search: string = globalThis.location?.search ?? "",
  hash: string = globalThis.location?.hash ?? "",
): void => {
  const win = globalThis.window as TrackerWindow | undefined;
  if (!win || (!search && !hash)) return;

  try {
    const entry = new URLSearchParams(search);
    const handoff = new URLSearchParams();
    for (const key of HANDOFF_KEYS) {
      const value = entry.get(key);
      if (value) handoff.set(key, value);
    }

    const token = new URLSearchParams(hash.replace(/^#/, "")).get(MESSAGE_LINK_KEY);
    if (token) {
      handoff.set(MESSAGE_LINK_KEY, token);
      const location = globalThis.location;
      const history = globalThis.history;
      if (location && typeof history?.replaceState === "function") {
        history.replaceState(
          history.state,
          "",
          `${location.pathname}${location.search}${fragmentWithoutToken(hash)}`,
        );
      }
    }

    const recorded = handoff.toString();
    if (recorded) win.__dappleEntrySearch = `?${recorded}`;
  } catch {
    // URLSearchParams unavailable: the tracker falls back to the live URL.
  }
};

recordEntryHandoff();

/** The tracker's script tag, whoever added it, or null. */
const findVisitorScript = (): HTMLScriptElement | null => {
  const doc = globalThis.document;
  if (typeof doc?.querySelector !== "function") return null;

  return (
    doc.querySelector<HTMLScriptElement>(`script[${MARKER_ATTRIBUTE}]`) ??
    doc.querySelector<HTMLScriptElement>(`script[src="${VISITOR_SCRIPT_SRC}"]`)
  );
};

/**
 * Appends `<script async src="/visitor.js">` to the document head unless the
 * tracker is already present or running. Returns true only when this call
 * added the tag. Safe to call where there is no DOM (it does nothing).
 */
export const injectVisitorScript = (): boolean => {
  const doc = globalThis.document;
  if (
    typeof doc?.createElement !== "function" ||
    typeof doc.querySelector !== "function"
  ) {
    return false;
  }

  if ((globalThis.window as TrackerWindow | undefined)?.__dappleTracker) {
    return false;
  }
  if (findVisitorScript()) return false;

  const script = doc.createElement("script");
  script.async = true;
  script.src = VISITOR_SCRIPT_SRC;
  script.setAttribute(MARKER_ATTRIBUTE, "true");
  script.addEventListener("load", () => {
    script.setAttribute(MARKER_ATTRIBUTE, "loaded");
  });
  (doc.head ?? doc.body)?.appendChild(script);

  return true;
};

/**
 * Calls `callback` once, when the tracker's script tag finishes loading, or
 * after `fallbackMs` if there is no tag to wait on or it never fires (a 404, a
 * blocker, a tag added by GTM that already loaded). Returns a cancel function.
 *
 * Used to retry a read of the visitor id: on a first visit the tracker mints
 * the id only once it has run, which is after the portal's first render.
 */
export const onVisitorScriptSettled = (
  callback: () => void,
  fallbackMs = 3000,
): (() => void) => {
  const script = findVisitorScript();
  const alreadyLoaded = script?.getAttribute(MARKER_ATTRIBUTE) === "loaded";
  const listenTo = script && !alreadyLoaded ? script : null;

  let done = false;

  const cancel = () => {
    done = true;
    // Only ever called after `timer` below is assigned (timeout or event).
    clearTimeout(timer);
    listenTo?.removeEventListener("load", fire);
    listenTo?.removeEventListener("error", fire);
  };

  function fire() {
    if (done) return;
    cancel();
    callback();
  }

  const timer = setTimeout(fire, alreadyLoaded ? 0 : fallbackMs);
  listenTo?.addEventListener("load", fire);
  listenTo?.addEventListener("error", fire);

  return cancel;
};
